import { MouseEventHandler, PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { apiBase } from "@/utils/urls";

type Data = {
  x: number;
  y: number;
  me: boolean;
  color: string;
  data: {
    companyId: number;
    companyName: string;
  };
};
export const ScatterPlot = ({
  disableMovement,
  url = "/api/crowd",
  ignoreColors,
}: PropsWithChildren<{ disableMovement?: boolean; url?: string; ignoreColors?: boolean }>) => {
  const container = useRef<SVGSVGElement>(null);
  const [initData, setData] = useState<Data[]>([]);
  const [percent, setPercent] = useState({ percentX: 50, percentY: 50 });

  // @ts-ignore
  const handleMouse = (e: MouseEventHandler<SVGSVGElement, MouseEvent>) => {
    const { width, height } = document.body.getBoundingClientRect();
    const percentX = 100 - Math.abs(((e.clientX - width) / width) * 100);
    const percentY = Math.abs(((e.clientY - height) / height) * 100);
    const currY = Math.abs(percent.percentY - percentY);
    const currX = Math.abs(percent.percentX - percentX);
    if (currY > 10 || currX > 10) {
      setPercent({ percentY, percentX });
    }

    return true;
  };
  const minX = useMemo(() => Math.min(...initData.map((d) => d.x)) - 2, [initData]);
  const maxX = useMemo(() => Math.max(...initData.map((d) => d.x)) + 2, [initData]);
  const minY = useMemo(() => Math.min(...initData.map((d) => d.y)) - 2, [initData]);
  const maxY = useMemo(() => Math.max(...initData.map((d) => d.y)) + 2, [initData]);

  const init = async () => {
    if (!container.current) {
      return;
    }
    let data = initData;
    if (!data.length) {
      const res = await fetch(apiBase + url);
      data = (await res.json()) as Data[];
      setData(data);
    }
    const path = data.filter((d) => d.color === "#818CF8");
    data = data.filter((d) => d.color !== "#818CF8");
    container.current.innerHTML = "";
    const { width, height } = container.current.getBoundingClientRect();
    const svg = d3.select(container.current).attr("width", width).attr("height", height);

    const x = d3.scaleLinear().domain([minX, maxX]).range([0, width]);
    svg
      .append("g")
      .style("opacity", 0)
      .attr("transform", "translate(0," + height + ")")
      .call(d3.axisBottom(x));
    const y = d3.scaleLinear().domain([minY, maxY]).range([height, 0]);

    svg.append("g").style("opacity", 0).call(d3.axisLeft(y));

    // create a tooltip
    d3.select("body")
      .append("div")
      .attr("id", "tooltip")
      .attr(
        "style",
        "position: absolute; opacity: 0; background: #4B5563; transform: translate(-50%, calc(-100% - 10px)); border-radius: 6px; padding: 6px 13px; color: #fff;"
      );

    svg
      .append("g")
      .selectAll("dot")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", function (d) {
        return x(d.x);
      })
      .attr("cy", function (d) {
        return y(d.y);
      })
      .attr("r", function (d) {
        if ((d.me || d.data) && !ignoreColors) {
          return 5.5;
        }

        if (disableMovement) {
          return 3;
        }

        const percentX = 100 - Math.abs(((d.x - maxX) / maxX) * 100);
        const percentY = 100 - Math.abs(((d.y - maxY) / maxY) * 100);

        const howCloseX = Math.abs(percent.percentX - percentX);
        const howCloseY = Math.abs(percent.percentY - percentY);

        const distance = howCloseY + howCloseX;

        if (!distance) {
          return 2;
        }
        const a = 2 + (50 - distance) / 30;
        if (a < 2) {
          return 2;
        }
        return a;
      })
      .style("fill", function (d) {
        if (ignoreColors) {
          return "#FB923C";
        }
        return d.me ? "#EF4444" : (d.color ?? "#FB923C");
      })
      .style("stroke", function (d) {
        if (ignoreColors) {
          return "";
        }
        return d.me ? "#000" : "";
      })
      .on("mouseover", function (event, d) {
        if (!d.data) {
          d3.select("#tooltip").transition().duration(200).style("opacity", 0);
          return;
        }
        d3.select("#tooltip")
          .transition()
          .duration(200)
          .style("opacity", 1)
          .text(d.data.companyName);
      })
      .on("mouseout", function () {
        d3.select("#tooltip").style("opacity", 0);
      })
      .on("mousemove", function (event, d) {
        if (!d.data) {
          d3.select("#tooltip").transition().duration(200).style("opacity", 0);
          return;
        }
        d3.select("#tooltip")
          .style("left", event.pageX + "px")
          .style("top", event.pageY + "px");
      });

    if (path && path.length) {
      let svgPath = `M ${x(path[0].x)} ${y(path[0].y)}`;

      // Use `L` commands for each subsequent point
      for (let i = 1; i < path.length; i++) {
        svgPath += ` L ${x(path[i].x)} ${y(path[i].y)}`;
      }
      svgPath += "Z";
      console.log(svgPath);
      svg
        .append("path")
        .attr("d", svgPath)
        .attr("fill", "rgba(59, 130, 246, 0.3)")
        .attr("stroke", "#818CF8");
    }

    // svg
    //   .selectAll("circle")
    //   .style("opacity", 0)
    //   .transition()
    //   .delay(function (d, i) {
    //     return i * 10;
    //   })
    //   .duration(1000)
    //   .style("opacity", 1);
  };
  useEffect(() => {
    init();
    window.addEventListener("resize", init);
    if (!disableMovement) {
      window.addEventListener("mousemove", handleMouse);
    }

    return () => {
      window.removeEventListener("resize", init);
      if (!disableMovement) {
        window.removeEventListener("mousemove", handleMouse);
      }
    };
  }, [percent, minX, maxX, minY, maxY, disableMovement]);

  return <svg ref={container} style={{ width: "100%", height: "100%" }} />;
};
